import { useCallback, useMemo, useState } from "react";
import { Eye, Clock, Search, Copy, RefreshCw } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";

import { ProcessamentoIADTO } from "@/types/processamento-ia.type";
import {
  EntidadeReferenciaProcessamentoIAMap,
  ProcessamentoIACriteria,
  ProcessamentoIAOptions,
  TipoProcessamentoIAMap,
} from "@workflow/types";
import { cn } from "@/lib/utils";

import { Column, DataTable } from "@/components/table/DataTable";
import { ActionMenu } from "@/components/shared/ActionMenu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FormCheckbox } from "@/components/campos-formulario/campo-checkbox";
import { ProcessamentoIAStatusBadge } from "@/components/processamentos-ia/ProcessamentoStatusBadge";

import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import {
  processamentoIaQueries,
  processamentoIaKeys,
} from "@/services/api/queries/processamento-ia.queries";
import {
  useReprocessarProcessamentoIA,
  useReprocessarLoteProcessamentoIA,
} from "@/hooks/processamento-ia/use-reprocessar-processamento-ia";
import { ProcessamentoIADetailsDialog } from "./ProcessamentoIADetailsDialog";

interface ProcessamentoIAListTableProps {
  storageKey: string;
}

export function ProcessamentoIAListTable({ storageKey }: ProcessamentoIAListTableProps) {
  const { tableProps, isError } = useServerDataTable<
    ProcessamentoIADTO,
    ProcessamentoIAOptions,
    ProcessamentoIACriteria
  >({
    queryOptions: processamentoIaQueries.list,
    filters: processamentoIaQueries.filtros,
    invalidateKey: processamentoIaKeys.all,
  });

  const reprocessar = useReprocessarProcessamentoIA();
  const reprocessarLote = useReprocessarLoteProcessamentoIA();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [detailId, setDetailId] = useState<number | null>(null);
  const openDetails = useCallback((proc: ProcessamentoIADTO) => setDetailId(proc.id), []);

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const renderSelectionColumn = useCallback(
    (proc: ProcessamentoIADTO) => (
      <div
        className="flex items-center justify-center"
        onClick={(e) => {
          e.stopPropagation();
          toggleSelect(proc.id);
        }}
      >
        <FormCheckbox
          id={`select-proc-${proc.id}`}
          label=""
          checked={selectedIds.includes(proc.id)}
          onChange={() => {}}
        />
      </div>
    ),
    [selectedIds, toggleSelect],
  );

  const handleReprocessarLote = useCallback(() => {
    if (!selectedIds.length || reprocessarLote.isPending) return;
    reprocessarLote.mutate(selectedIds, { onSuccess: () => setSelectedIds([]) });
  }, [selectedIds, reprocessarLote]);

  const bulkAction =
    selectedIds.length > 0 ? (
      <Button
        size="sm"
        onClick={handleReprocessarLote}
        disabled={reprocessarLote.isPending}
        className="gap-2"
      >
        <RefreshCw className={cn("h-4 w-4", reprocessarLote.isPending && "animate-spin")} />
        Reprocessar {selectedIds.length} selecionado(s)
      </Button>
    ) : undefined;

  const columns = useMemo<Column<ProcessamentoIADTO>[]>(
    () => [
      {
        type: "headless",
        key: "actions",
        title: "Ações",
        visible: true,
        width: "w-[50px]",
        render: (proc) => (
          <ActionMenu
            items={[
              {
                label: "Visualizar Payloads",
                icon: <Eye className="h-4 w-4" />,
                onClick: () => openDetails(proc),
              },
              {
                label: "Reprocessar",
                icon: <RefreshCw className="h-4 w-4" />,
                onClick: () => {
                  if (!reprocessar.isPending) reprocessar.mutate(proc.id);
                },
              },
              { label: "Separador", separator: true },
              {
                label: "Copiar ID do Processamento",
                icon: <Copy className="h-4 w-4" />,
                onClick: () => {
                  navigator.clipboard.writeText(proc.id.toString());
                  toast.success(`ID #${proc.id} copiado!`);
                },
              },
            ]}
          />
        ),
      },
      {
        key: "processamentoIA.id",
        title: "ID",
        sortable: true,
        width: "min-w-[90px]",
        render: (proc) => (
          <button
            onClick={() => openDetails(proc)}
            className="group flex items-center gap-1.5 rounded-md px-2 py-1 font-semibold text-primary transition-colors hover:bg-primary/10"
            title="Clique para ver detalhes"
          >
            <Search className="h-3.5 w-3.5 opacity-50 transition-opacity group-hover:opacity-100" />
            {proc.id}
          </button>
        ),
        raw: (proc) => proc.id.toString(),
      },
      {
        key: "processamentoIA.tipoProcessamento",
        title: "Tipo de Processamento",
        sortable: true,
        width: "min-w-[200px]",
        render: (proc) => (
          <span className="font-medium capitalize text-foreground">
            {TipoProcessamentoIAMap[proc.tipoProcessamento] || proc.tipoProcessamento}
          </span>
        ),
        raw: (proc) => proc.tipoProcessamento,
      },
      {
        key: "processamentoIA.entidade",
        title: "Referência",
        sortable: false,
        width: "min-w-[150px]",
        render: (proc) => {
          const sigla =
            EntidadeReferenciaProcessamentoIAMap[proc.entidadeTipo] || proc.entidadeTipo;
          const refText = `${sigla}-${proc.entidadeId}`;

          return (
            <div
              className="group/ref flex cursor-pointer items-center gap-2"
              onClick={() => {
                navigator.clipboard.writeText(proc.entidadeId.toString());
                toast.success(`Referência ${refText} copiada!`);
              }}
              title="Clique para copiar a referência"
            >
              <Badge
                variant="outline"
                className="border-border/80 bg-muted/30 font-mono text-xs tracking-wide text-muted-foreground transition-colors group-hover/ref:border-primary/50 group-hover/ref:text-primary"
              >
                {sigla} - #{proc.entidadeId}
              </Badge>
              <Copy className="h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover/ref:opacity-100" />
            </div>
          );
        },
        raw: (proc) => `${proc.entidadeTipo}-${proc.entidadeId}`,
      },
      {
        key: "processamentoIA.status",
        title: "Status",
        sortable: true,
        width: "min-w-[130px]",
        render: (proc) => <ProcessamentoIAStatusBadge status={proc.status} />,
        raw: (proc) => proc.status,
      },
      {
        key: "processamentoIA.criadoEm",
        title: "Data de Criação",
        sortable: true,
        width: "min-w-[160px]",
        render: (proc) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {proc.criadoEm
              ? format(new Date(proc.criadoEm), "dd 'de' MMM, HH:mm", { locale: ptBR })
              : "-"}
          </span>
        ),
        raw: (proc) => proc.criadoEm?.toString() || "",
      },
      {
        key: "processamentoIA.dataConclusao",
        title: "Data de Conclusão",
        sortable: true,
        width: "min-w-[160px]",
        render: (proc) => {
          if (!proc.dataConclusao) return <span className="text-sm text-muted-foreground">-</span>;
          const distance = formatDistanceToNow(new Date(proc.dataConclusao), {
            addSuffix: true,
            locale: ptBR,
          });

          return (
            <div
              className="flex w-fit cursor-default items-center gap-1.5 rounded-md border border-border/30 bg-muted/20 px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted/50"
              title={format(new Date(proc.dataConclusao), "dd/MM/yyyy HH:mm:ss")}
            >
              <Clock className="h-3.5 w-3.5 text-primary/70" />
              <span className="capitalize-first truncate">{distance}</span>
            </div>
          );
        },
        raw: (proc) => proc.dataConclusao?.toString() || "",
      },
    ],
    [openDetails, reprocessar],
  );

  return (
    <>
      <DataTable
        {...tableProps}
        storageKey={storageKey}
        columns={columns}
        emptyMessage={
          isError
            ? "Não foi possível carregar os registros de IA. Tente recarregar."
            : "Nenhum registro de IA encontrado na base de dados."
        }
        defaultSortColumn="processamentoIA.id"
        defaultSortOrder="DESC"
        searchPlaceholder="Pesquisar por ID, Tipo ou Entidade..."
        className="border-none"
        actionButton={bulkAction}
        renderSelectionColumn={renderSelectionColumn}
        rowClassName={(proc) => {
          if (proc.status === "FALHA")
            return "bg-destructive/5 border-l-2 border-l-destructive/50 hover:bg-destructive/10";
          if (proc.status === "PENDENTE")
            return "bg-amber-500/5 border-l-2 border-l-amber-500/50 hover:bg-amber-500/10";
          return "hover:bg-muted/50";
        }}
      />

      <ProcessamentoIADetailsDialog
        open={detailId !== null}
        onOpenChange={(open) => !open && setDetailId(null)}
        processamentoID={detailId}
      />
    </>
  );
}
